import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Search, Package, MapPin, RefreshCw, 
  AlertTriangle, CheckCircle, Unlock, Edit2, Save, X,
  BarChart3, Filter, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const FootprintCapacity = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [footprints, setFootprints] = useState([]);
  const [summary, setSummary] = useState({});
  const [searchDesign, setSearchDesign] = useState('');
  const [searchBin, setSearchBin] = useState('');
  const [showFullOnly, setShowFullOnly] = useState(false);
  const [showEmptyOnly, setShowEmptyOnly] = useState(false);
  const [includeEmpty, setIncludeEmpty] = useState(true);
  
  // Release bin dialog
  const [releaseDialog, setReleaseDialog] = useState(false);
  const [selectedBin, setSelectedBin] = useState(null);
  const [newCapacity, setNewCapacity] = useState('');
  const [releasing, setReleasing] = useState(false);
  
  // Edit capacity dialog
  const [editDialog, setEditDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editCapacity, setEditCapacity] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchFootprints = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchDesign) params.append('design_name', searchDesign);
      if (searchBin) params.append('bin_code', searchBin);
      params.append('include_empty', includeEmpty.toString());
      
      const response = await apiClient.get(`/wms/fg/put-away/footprint-capacity?${params.toString()}`);
      
      let data = response.data.footprints || [];
      if (showFullOnly) {
        data = data.filter(f => f.is_full);
      }
      if (showEmptyOnly) {
        data = data.filter(f => f.is_empty);
      }
      
      setFootprints(data);
      setSummary(response.data.summary || {});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load footprint data');
    } finally {
      setLoading(false);
    }
  }, [searchDesign, searchBin, showFullOnly, showEmptyOnly, includeEmpty]);

  useEffect(() => {
    fetchFootprints();
  }, [fetchFootprints]);

  const handleReleaseBin = async () => {
    if (!selectedBin) return;
    
    try {
      setReleasing(true);
      const params = new URLSearchParams({ bin_code: selectedBin.bin_code });
      if (newCapacity && parseInt(newCapacity) > 0) {
        params.append('new_capacity', newCapacity);
      }
      
      const response = await apiClient.post(`/wms/fg/put-away/release-bin-full?${params.toString()}`);
      
      toast.success(response.data.message);
      setReleaseDialog(false);
      setSelectedBin(null);
      setNewCapacity('');
      fetchFootprints();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to release bin');
    } finally {
      setReleasing(false);
    }
  };

  const handleUpdateCapacity = async () => {
    if (!editItem || !editCapacity) return;
    
    try {
      setSaving(true);
      const response = await apiClient.put('/wms/fg/put-away/update-bin-capacity', null, {
        params: {
          design_name: editItem.design_name,
          bin_type: editItem.bin_type,
          new_capacity: parseInt(editCapacity)
        }
      });
      
      toast.success(response.data.message);
      setEditDialog(false);
      setEditItem(null);
      setEditCapacity('');
      fetchFootprints();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update capacity');
    } finally {
      setSaving(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Bin Code', 'Bin Type', 'Design Name', 'Shade', 'Size', 'Current Stock', 'Total Capacity', 'Available', 'Status'];
    const rows = footprints.map(f => [
      f.bin_code,
      f.bin_type,
      f.design_name,
      f.shade,
      f.size,
      f.current_stock,
      f.total_capacity || 'N/A',
      f.available_capacity ?? 'N/A',
      f.is_full ? 'FULL' : 'Active'
    ]);
    
    const csvContent = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `footprint_capacity_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/fg')}
              className="text-white hover:bg-white/10"
              data-testid="footprint-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <BarChart3 className="w-7 h-7" />
              <div>
                <h1 className="text-xl font-bold">Footprint Capacity</h1>
                <p className="text-xs text-gray-300">View & manage bin capacities</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={exportToCSV}
              className="text-white border-white/30 hover:bg-white/10"
            >
              <Download className="w-4 h-4 mr-1" />
              Export
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchFootprints}
              className="text-white border-white/30 hover:bg-white/10"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Total Bins</p>
                  <p className="text-2xl font-bold text-blue-800">{summary.total_bins || 0}</p>
                </div>
                <MapPin className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Occupied</p>
                  <p className="text-2xl font-bold text-green-800">{summary.occupied_bins || 0}</p>
                </div>
                <Package className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Empty</p>
                  <p className="text-2xl font-bold text-gray-800">{summary.empty_bins || 0}</p>
                </div>
                <MapPin className="w-8 h-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-red-50 border-red-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Full Bins</p>
                  <p className="text-2xl font-bold text-red-800">{summary.full_bins || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600">Total Pieces</p>
                  <p className="text-2xl font-bold text-purple-800">{summary.total_pieces_stored?.toLocaleString() || 0}</p>
                </div>
                <BarChart3 className="w-8 h-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Hall Average Capacities */}
        {summary.hall_avg_capacities && Object.keys(summary.hall_avg_capacities).length > 0 && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-4">
              <p className="text-sm font-medium text-amber-700 mb-2">Hall Average Capacities (for empty bins)</p>
              <div className="flex flex-wrap gap-3">
                {Object.entries(summary.hall_avg_capacities).sort().map(([hall, avg]) => (
                  <Badge key={hall} variant="outline" className="bg-white border-amber-300 text-amber-800">
                    {hall}: {avg} pcs
                  </Badge>
                ))}
                <Badge variant="outline" className="bg-amber-100 border-amber-400 text-amber-900 font-bold">
                  Overall Avg: {summary.overall_avg_capacity} pcs
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by design name..."
                    value={searchDesign}
                    onChange={(e) => setSearchDesign(e.target.value)}
                    className="pl-10"
                    data-testid="search-design-input"
                  />
                </div>
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by bin code..."
                    value={searchBin}
                    onChange={(e) => setSearchBin(e.target.value)}
                    className="pl-10"
                    data-testid="search-bin-input"
                  />
                </div>
              </div>
              <Button
                variant={includeEmpty ? "default" : "outline"}
                onClick={() => {
                  setIncludeEmpty(!includeEmpty);
                  setShowEmptyOnly(false);
                }}
                className={includeEmpty ? "bg-gray-600 hover:bg-gray-700" : ""}
              >
                {includeEmpty ? 'Including Empty' : 'Exclude Empty'}
              </Button>
              <Button
                variant={showEmptyOnly ? "default" : "outline"}
                onClick={() => {
                  setShowEmptyOnly(!showEmptyOnly);
                  if (!showEmptyOnly) {
                    setShowFullOnly(false);
                    setIncludeEmpty(true);
                  }
                }}
                className={showEmptyOnly ? "bg-gray-500 hover:bg-gray-600" : ""}
                disabled={!includeEmpty}
              >
                Empty Only
              </Button>
              <Button
                variant={showFullOnly ? "default" : "outline"}
                onClick={() => {
                  setShowFullOnly(!showFullOnly);
                  if (!showFullOnly) setShowEmptyOnly(false);
                }}
                className={showFullOnly ? "bg-red-500 hover:bg-red-600" : ""}
              >
                <Filter className="w-4 h-4 mr-2" />
                Full Only
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footprint Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Footprint Details ({footprints.length} records)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-10 text-gray-500">Loading...</div>
            ) : footprints.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                No footprint data found. Put away some items first.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bin Code</TableHead>
                      <TableHead>Design</TableHead>
                      <TableHead>Shade / Size</TableHead>
                      <TableHead className="text-center">Current Stock</TableHead>
                      <TableHead className="text-center">Total Capacity</TableHead>
                      <TableHead className="text-center">Available</TableHead>
                      <TableHead className="text-center">Usage</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {footprints.map((fp, idx) => (
                      <TableRow 
                        key={`${fp.bin_code}-${fp.design_name}-${idx}`}
                        className={fp.is_full ? 'bg-red-50' : fp.is_empty ? 'bg-gray-50' : ''}
                        data-testid={`footprint-row-${idx}`}
                      >
                        <TableCell className="font-mono font-bold">
                          <div className="flex items-center gap-2">
                            <MapPin className={`w-4 h-4 ${fp.is_empty ? 'text-gray-300' : 'text-gray-400'}`} />
                            {fp.bin_code}
                            {fp.hall && (
                              <Badge variant="outline" className="text-xs bg-gray-100">
                                {fp.hall}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className={`font-medium ${fp.is_empty ? 'text-gray-400 italic' : ''}`}>
                          {fp.design_name || '(Empty)'}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {fp.shade || '-'} / {fp.size || '-'}
                        </TableCell>
                        <TableCell className="text-center font-bold text-lg">
                          {fp.current_stock}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex flex-col items-center">
                            <span>{fp.total_capacity || '-'}</span>
                            {fp.capacity_source && fp.capacity_source !== 'learned' && (
                              <span className="text-xs text-amber-600">{fp.capacity_source}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {fp.available_capacity !== null ? (
                            <span className={fp.available_capacity <= 0 ? 'text-red-600 font-bold' : 'text-green-600'}>
                              {fp.available_capacity}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center w-32">
                          {fp.capacity_percentage !== null ? (
                            <div className="space-y-1">
                              <Progress 
                                value={Math.min(fp.capacity_percentage, 100)} 
                                className={`h-2 ${fp.capacity_percentage >= 100 ? '[&>div]:bg-red-500' : fp.capacity_percentage >= 80 ? '[&>div]:bg-amber-500' : fp.capacity_percentage === 0 ? '[&>div]:bg-gray-300' : '[&>div]:bg-green-500'}`}
                              />
                              <span className="text-xs text-gray-500">{fp.capacity_percentage}%</span>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {fp.is_full ? (
                            <Badge className="bg-red-500 text-white">FULL</Badge>
                          ) : fp.is_empty ? (
                            <Badge variant="outline" className="border-gray-300 text-gray-500">
                              Empty
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-green-500 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Active
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            {fp.is_full && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedBin(fp);
                                  setNewCapacity(fp.total_capacity?.toString() || '');
                                  setReleaseDialog(true);
                                }}
                                className="text-green-600 border-green-300 hover:bg-green-50"
                                title="Release bin for more items"
                                data-testid={`release-btn-${idx}`}
                              >
                                <Unlock className="w-4 h-4" />
                              </Button>
                            )}
                            {!fp.is_empty && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setEditItem(fp);
                                  setEditCapacity(fp.total_capacity?.toString() || '');
                                  setEditDialog(true);
                                }}
                                className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                title="Edit capacity"
                                data-testid={`edit-btn-${idx}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Release Bin Dialog */}
      <Dialog open={releaseDialog} onOpenChange={setReleaseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Unlock className="w-5 h-5 text-green-600" />
              Release Bin Full Status
            </DialogTitle>
            <DialogDescription>
              This will allow more items to be placed in this bin.
            </DialogDescription>
          </DialogHeader>
          
          {selectedBin && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Bin Code:</span>
                  <span className="font-mono font-bold">{selectedBin.bin_code}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Design:</span>
                  <span className="font-medium">{selectedBin.design_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Current Stock:</span>
                  <span className="font-bold">{selectedBin.current_stock} pcs</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Current Capacity:</span>
                  <span className="font-bold">{selectedBin.total_capacity || 'Not Set'}</span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">New Capacity (optional)</label>
                <Input
                  type="number"
                  placeholder="Leave empty to keep current"
                  value={newCapacity}
                  onChange={(e) => setNewCapacity(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Set a new max capacity for this design + bin type combination
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setReleaseDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReleaseBin} 
              disabled={releasing}
              className="bg-green-600 hover:bg-green-700"
            >
              {releasing ? 'Releasing...' : 'Release Bin'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Capacity Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-blue-600" />
              Edit Bin Capacity
            </DialogTitle>
            <DialogDescription>
              Update the learned capacity for this design + bin type.
            </DialogDescription>
          </DialogHeader>
          
          {editItem && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Design:</span>
                  <span className="font-medium">{editItem.design_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Bin Type:</span>
                  <span className="font-mono font-bold">{editItem.bin_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Current Capacity:</span>
                  <span className="font-bold">{editItem.total_capacity || 'Not Set'}</span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">New Capacity *</label>
                <Input
                  type="number"
                  placeholder="Enter new capacity"
                  value={editCapacity}
                  onChange={(e) => setEditCapacity(e.target.value)}
                  className="mt-1"
                  min="1"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateCapacity} 
              disabled={saving || !editCapacity}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? 'Saving...' : 'Save Capacity'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FootprintCapacity;
